import React from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import CodeBlock from "@/components/CodeBlock";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";
import { ChevronRight } from "lucide-react";

function SdkTabs() {
  const [lang, setLang] = React.useState("curl");
  const examples = {
    curl: `curl -H "X-API-Key: $LOFTPRESS_KEY" \\
  "https://loftpress.shop/api/v1/currency/convert?base=USD&to=EUR&amount=100"`,
    js: `const res = await fetch("https://loftpress.shop/api/v1/currency/convert?base=USD&to=EUR&amount=100", {
  headers: { "X-API-Key": process.env.LOFTPRESS_KEY }
});
const { data } = await res.json();
console.log(data.result);`,
    py: `import os, requests

res = requests.get(
  "https://loftpress.shop/api/v1/currency/convert",
  params={"base": "USD", "to": "EUR", "amount": 100},
  headers={"X-API-Key": os.environ["LOFTPRESS_KEY"]},
)
print(res.json()["data"]["result"])`,
    php: `<?php
$url = "https://loftpress.shop/api/v1/currency/convert?base=USD&to=EUR&amount=100";
$ch = curl_init($url);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-Key: " . getenv("LOFTPRESS_KEY")]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$resp = json_decode(curl_exec($ch), true);
echo $resp["data"]["result"];`,
  };
  const map = { curl: "cURL", js: "JavaScript", py: "Python", php: "PHP" };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {Object.keys(examples).map((k) => (
          <button
            key={k}
            className={`text-xs px-3 py-1.5 rounded-full font-mono ${lang === k ? "bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37]" : "border border-white/10 text-white/60"}`}
            onClick={() => setLang(k)}
            data-testid={`docs-lang-${k}`}
          >{map[k]}</button>
        ))}
      </div>
      <CodeBlock code={examples[lang]} language={lang} testId={`docs-code-${lang}`} />
    </div>
  );
}

export default function DocsPage() {
  const { section } = useParams();
  const { t } = useLang();
  const [toc, setToc] = React.useState({ sections: [], products: [] });
  React.useEffect(() => { api.get("/api/docs/toc").then((r) => setToc(r.data)); }, []);
  const activeKey = section || "getting-started";

  const SECTIONS = {
    "getting-started": {
      title: t("docs.getting_started.title"),
      body: (
        <>
          <p className="text-white/70 leading-relaxed">{t("docs.getting_started.intro")}</p>
          <ol className="mt-4 list-decimal pl-6 space-y-2 text-white/70">
            <li>{t("docs.getting_started.step1")}</li>
            <li>{t("docs.getting_started.step2_pre")}{" "}
              <Link className="text-[#d4af37]" to="/dashboard/api-keys">{t("docs.getting_started.step2_link")}</Link>.</li>
            <li>{t("docs.getting_started.step3")}</li>
            <li>{t("docs.getting_started.step4")}</li>
          </ol>
          <CodeBlock className="mt-4" code={`curl -H "X-API-Key: $LOFTPRESS_KEY" \\
  https://loftpress.shop/api/v1/currency/latest?base=USD`} language="bash" testId="doc-getting-started" />
        </>
      ),
    },
    authentication: {
      title: t("docs.auth.title"),
      body: (
        <>
          <p className="text-white/70">{t("docs.auth.body")}</p>
          <CodeBlock code={`# ${t("docs.auth.without_key")}
$ curl https://loftpress.shop/api/v1/currency/latest
{
  "detail": {
    "success": false,
    "error": { "code": "missing_api_key", "message": "Provide an API key via X-API-Key header." },
    "request_id": "req_abc123"
  }
}`} language="bash" />
        </>
      ),
    },
    "rate-limits": {
      title: t("docs.rate.title"),
      body: (
        <>
          <p className="text-white/70">{t("docs.rate.body")}</p>
          <ul className="mt-3 space-y-1 text-white/70 list-disc pl-6">
            <li><strong>{t("docs.rate.trial")}</strong>: {t("docs.rate.trial_desc")}</li>
            <li><strong>{t("docs.rate.pro")}</strong>: {t("docs.rate.pro_desc")}</li>
            <li><strong>{t("docs.rate.ent")}</strong>: {t("docs.rate.ent_desc")}</li>
          </ul>
          <p className="text-white/70 mt-3">{t("docs.rate.exceed")}</p>
        </>
      ),
    },
    errors: {
      title: t("docs.errors.title"),
      body: (
        <table className="lp-table mt-4">
          <thead><tr><th>HTTP</th><th>{t("docs.errors.code")}</th><th>{t("docs.errors.meaning")}</th></tr></thead>
          <tbody>
            <tr><td>401</td><td className="font-mono text-[#d4af37]">missing_api_key</td><td>{t("docs.errors.401_missing")}</td></tr>
            <tr><td>401</td><td className="font-mono text-[#d4af37]">invalid_api_key</td><td>{t("docs.errors.401_invalid")}</td></tr>
            <tr><td>402</td><td className="font-mono text-[#d4af37]">quota_exceeded</td><td>{t("docs.errors.402_quota")}</td></tr>
            <tr><td>402</td><td className="font-mono text-[#d4af37]">subscription_inactive</td><td>{t("docs.errors.402_sub")}</td></tr>
            <tr><td>403</td><td className="font-mono text-[#d4af37]">wrong_product</td><td>{t("docs.errors.403_wrong")}</td></tr>
            <tr><td>403</td><td className="font-mono text-[#d4af37]">ip_not_allowed</td><td>{t("docs.errors.403_ip")}</td></tr>
            <tr><td>429</td><td className="font-mono text-[#d4af37]">rate_limited</td><td>{t("docs.errors.429")}</td></tr>
            <tr><td>503</td><td className="font-mono text-[#d4af37]">maintenance</td><td>{t("docs.errors.503")}</td></tr>
          </tbody>
        </table>
      ),
    },
    webhooks: {
      title: t("docs.webhooks.title"),
      body: (
        <>
          <p className="text-white/70">{t("docs.webhooks.body1_pre")}{" "}
            <Link className="text-[#d4af37]" to="/dashboard/settings">{t("docs.webhooks.body1_link")}</Link>. {t("docs.webhooks.body1_post")}</p>
          <p className="text-white/70 mt-3">{t("docs.webhooks.body2")}</p>
        </>
      ),
    },
    sdk: {
      title: t("docs.sdk.title"),
      body: <SdkTabs />,
    },
  };
  const active = SECTIONS[activeKey] || SECTIONS["getting-started"];

  return (
    <div>
      <Header />
      <Seo title={t("seo.docs.title")} description={t("seo.docs.desc")} path="/docs" />
      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lp-card p-4 h-fit lg:sticky lg:top-24">
          <div className="text-xs uppercase tracking-widest text-white/40 px-2 pb-3">{t("docs.guides")}</div>
          {toc.sections.map((s) => (
            <Link
              key={s.key}
              to={`/docs/${s.key}`}
              className={`lp-sidebar-link ${activeKey === s.key ? "active" : ""}`}
              data-testid={`docs-nav-${s.key}`}
            >
              <ChevronRight className="w-3.5 h-3.5" /> {SECTIONS[s.key]?.title || s.title}
            </Link>
          ))}
          <div className="text-xs uppercase tracking-widest text-white/40 px-2 pt-6 pb-3">{t("docs.products")}</div>
          {toc.products.map((p) => (
            <Link key={p.slug} to={`/products/${p.slug}`} className="lp-sidebar-link" data-testid={`docs-product-${p.slug}`}>
              <ChevronRight className="w-3.5 h-3.5" /> {displayProductName(p.name)}
            </Link>
          ))}
        </aside>
        <article className="lp-card p-8" data-testid="docs-content">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("docs.eyebrow")}</div>
          <h1 className="font-display text-4xl text-white/95">{active.title}</h1>
          <div className="mt-6 prose prose-invert">{active.body}</div>
        </article>
      </div>
      <Footer />
    </div>
  );
}
